import { useState } from 'react';
import { Stethoscope, Play, CheckCircle, XCircle, AlertCircle, RefreshCw, Info, Wallet, Zap, ArrowLeftRight, SendHorizonal, Coins } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { apiRequest } from '@/lib/queryClient';

interface ProbeConfig {
  kudoz_api_url: string;
  key_source: string;
  key_prefix: string;
  key_length: number;
  system_user_id: number;
}

interface ProbeStep {
  status: number;
  ok: boolean;
  body: any;
  userId?: number | null;
  networkError?: string;
}

interface ProbeResult {
  config: ProbeConfig;
  steps: {
    health: ProbeStep;
    get_user: ProbeStep;
    post_user: ProbeStep;
    system_wallet: ProbeStep;
  };
  error?: string;
}

interface FundResult {
  success: boolean;
  created: boolean;
  previous_balance: number;
  wallet: {
    wallet_id: string;
    user_id: number;
    balance: number;
    wallet_type: string;
  };
}

interface MigrateResult {
  success: boolean;
  dev_wallet: FundResult & { user_id: number };
  prod_wallet: FundResult & { user_id: number };
}

interface SendResult {
  success: boolean;
  email: string;
  amount: number;
  reason: string;
  eatzyUserId: string | null;
  kudozUserId: number;
  transactionId: string | null;
  newBalance: number | null;
}

function StatusBadge({ step }: { step: ProbeStep }) {
  if (step.networkError) {
    return <Badge variant="destructive">Network Error</Badge>;
  }
  if (step.status === 0) {
    return <Badge variant="destructive">No Response</Badge>;
  }
  if (step.ok) {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">{step.status} OK</Badge>;
  }
  if (step.status >= 400 && step.status < 500) {
    return <Badge variant="destructive">{step.status} Auth Error</Badge>;
  }
  return <Badge variant="destructive">{step.status} Error</Badge>;
}

function StepRow({
  label,
  description,
  step,
  extra,
}: {
  label: string;
  description: string;
  step: ProbeStep;
  extra?: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border rounded-md overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/30">
        {step.ok ? (
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        ) : (
          <XCircle className="w-5 h-5 text-destructive flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
          {extra}
        </div>
        <StatusBadge step={step} />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-muted-foreground"
        >
          {expanded ? 'Hide' : 'Show'} response
        </Button>
      </div>
      {expanded && (
        <div className="px-4 py-3 bg-muted/10 border-t">
          {step.networkError ? (
            <p className="text-xs text-destructive font-mono">{step.networkError}</p>
          ) : (
            <pre className="text-xs font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto text-foreground/80">
              {JSON.stringify(step.body, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function formatBalance(n: number | undefined | null): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

export default function AdminKudozProbe() {
  const [userId, setUserId] = useState('146');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<ProbeResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const [isFunding, setIsFunding] = useState(false);
  const [fundResult, setFundResult] = useState<FundResult | null>(null);
  const [fundError, setFundError] = useState<string | null>(null);

  const [isMigrating, setIsMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState<MigrateResult | null>(null);
  const [migrateError, setMigrateError] = useState<string | null>(null);

  // Send Kudoz state
  const [sendEmail, setSendEmail] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendReason, setSendReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const allOk = result && Object.values(result.steps).every((s) => s.ok);
  const anyFailed = result && Object.values(result.steps).some((s) => !s.ok);

  const systemUserId: number = result?.config?.system_user_id ?? 53884;

  const systemWalletBalance: number | null = result?.steps?.system_wallet?.ok
    ? (result.steps.system_wallet.body?.wallet?.balance ?? result.steps.system_wallet.body?.balance ?? null)
    : null;

  async function runProbe() {
    setIsRunning(true);
    setResult(null);
    setRunError(null);
    setFundResult(null);
    setFundError(null);
    try {
      const id = parseInt(userId, 10) || 146;
      const data = await apiRequest('GET', `/api/admin/kudoz/probe?userId=${id}`);
      const json = await data.json();
      setResult(json);
    } catch (err: any) {
      setRunError(err?.message || 'Request failed');
    } finally {
      setIsRunning(false);
    }
  }

  async function fundSystemWallet() {
    setIsFunding(true);
    setFundResult(null);
    setFundError(null);
    try {
      const data = await apiRequest('POST', '/api/admin/kudoz/fund-system-wallet');
      const json = await data.json();
      if (!json.success && !json.wallet) {
        setFundError(json.error || 'Fund request returned no wallet data');
      } else {
        setFundResult(json);
      }
    } catch (err: any) {
      setFundError(err?.message || 'Fund request failed');
    } finally {
      setIsFunding(false);
    }
  }

  async function migrateSystemWallets() {
    setIsMigrating(true);
    setMigrateResult(null);
    setMigrateError(null);
    try {
      const data = await apiRequest('POST', '/api/admin/kudoz/migrate-system-wallets');
      const json = await data.json();
      if (!json.success) {
        setMigrateError(json.error || 'Migration returned unexpected result');
      } else {
        setMigrateResult(json);
      }
    } catch (err: any) {
      setMigrateError(err?.message || 'Migration request failed');
    } finally {
      setIsMigrating(false);
    }
  }

  // Validate form fields before opening confirm dialog
  function handleSendClick() {
    setSendError(null);
    setSendResult(null);
    const emailTrimmed = sendEmail.trim();
    // Strict integer check — Number("1.9") is not an integer; parseInt("1.9") would silently truncate.
    const amountNum = Number(sendAmount);
    if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setSendError('Please enter a valid email address.');
      return;
    }
    if (!sendAmount || isNaN(amountNum) || !Number.isInteger(amountNum) || amountNum <= 0) {
      setSendError('Amount must be a positive whole number (no decimals).');
      return;
    }
    setConfirmOpen(true);
  }

  async function executeSend() {
    setIsSending(true);
    setSendError(null);
    setConfirmOpen(false);
    try {
      const amountNum = Number(sendAmount);

      // Use raw fetch so we can inspect non-2xx bodies before deciding how to display the error.
      // apiRequest throws on non-2xx and consumes the response body, making status-specific
      // error handling impossible after the call.
      const token = window.localStorage.getItem('auth_token');
      const res = await fetch('/api/admin/kudoz/send-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sendEmail.trim(),
          amount: amountNum,
          reason: sendReason.trim() || undefined,
        }),
      });

      let json: any;
      try {
        json = await res.json();
      } catch {
        json = {};
      }

      if (!res.ok) {
        if (res.status === 422) {
          // Wallet not linked — server error field already includes the repair-link instruction
          setSendError(json.error || 'Wallet not linked. Run POST /api/admin/kudoz/repair-link first.');
        } else {
          setSendError(json.error || `Request failed (${res.status}). Check the server logs.`);
        }
        return;
      }

      if (!json.success) {
        setSendError(json.error || 'Send failed with an unknown error.');
      } else {
        setSendResult(json);
        setSendEmail('');
        setSendAmount('');
        setSendReason('');
      }
    } catch (err: any) {
      setSendError(err?.message || 'Request failed. Check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <div className="flex items-start gap-3">
          <Stethoscope className="w-6 h-6 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <h1 className="text-xl font-semibold">Kudoz API Diagnostics</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Runs live test calls to the Kudoz backend and shows the exact HTTP status and response body for each step. Use this to pinpoint credential issues and to manage system wallets.
            </p>
          </div>
        </div>

        {/* Send Kudoz Card — always visible */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-base">Send Kudoz to User</CardTitle>
            </div>
            <CardDescription>
              Send any amount of Kudoz tokens from the Eatzy system wallet directly to a user wallet by email address.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="send-email">Recipient email</Label>
                <Input
                  id="send-email"
                  type="email"
                  value={sendEmail}
                  onChange={(e) => setSendEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="mt-1"
                  data-testid="input-send-email"
                  disabled={isSending}
                />
              </div>
              <div>
                <Label htmlFor="send-amount">Amount (Kudoz)</Label>
                <Input
                  id="send-amount"
                  type="number"
                  min="1"
                  step="1"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  placeholder="100"
                  className="mt-1"
                  data-testid="input-send-amount"
                  disabled={isSending}
                />
              </div>
              <div>
                <Label htmlFor="send-reason">Reason / note <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  id="send-reason"
                  type="text"
                  value={sendReason}
                  onChange={(e) => setSendReason(e.target.value)}
                  placeholder="e.g. Compensation for order issue"
                  className="mt-1"
                  data-testid="input-send-reason"
                  disabled={isSending}
                />
              </div>
            </div>

            <Button
              onClick={handleSendClick}
              disabled={isSending || !sendEmail || !sendAmount}
              data-testid="button-send-kudoz"
            >
              <SendHorizonal className="w-4 h-4 mr-2" />
              Send Kudoz
            </Button>

            {sendError && (
              <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Send failed</p>
                  <p className="text-xs mt-0.5 whitespace-pre-line">{sendError}</p>
                </div>
              </div>
            )}

            {sendResult && (
              <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-green-600/10 border border-green-600/20 text-green-700 dark:text-green-400">
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {formatBalance(sendResult.amount)} Kudoz sent to {sendResult.email}
                  </p>
                  {sendResult.transactionId && (
                    <p className="text-xs font-mono opacity-80">Tx ID: {sendResult.transactionId}</p>
                  )}
                  {sendResult.newBalance != null && (
                    <p className="text-xs">
                      Recipient new balance: <span className="font-semibold font-mono">{formatBalance(sendResult.newBalance)} Kudoz</span>
                    </p>
                  )}
                  {sendResult.reason && (
                    <p className="text-xs opacity-80">Reason: {sendResult.reason}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onOpenChange={(open) => { if (!isSending) setConfirmOpen(open); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Kudoz Transfer</DialogTitle>
              <DialogDescription>
                Please review the details below before confirming. This action will immediately debit the Eatzy system wallet.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                <span className="text-muted-foreground">Recipient</span>
                <span className="font-medium break-all">{sendEmail}</span>
                <span className="text-muted-foreground">Amount</span>
                <span className="font-semibold text-base">{Number(sendAmount).toLocaleString()} Kudoz</span>
                <span className="text-muted-foreground">Source</span>
                <span>Eatzy system wallet</span>
                <span className="text-muted-foreground">Reason</span>
                <span className="break-all">{sendReason.trim() || <span className="text-muted-foreground italic">No reason provided</span>}</span>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button
                onClick={executeSend}
                disabled={isSending}
                data-testid="button-confirm-send-kudoz"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <SendHorizonal className="w-4 h-4 mr-2" />
                    Confirm Send
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                This is a temporary diagnostic tool. The POST /users step will attempt to create a test user in the Kudoz system — harmless if the user already exists. Remove this page once the credential issue is fully resolved.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Run Diagnostic</CardTitle>
            <CardDescription>Enter any Eatzy user ID to test against, then click Run.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="probe-user-id">Eatzy User ID</Label>
                <Input
                  id="probe-user-id"
                  type="number"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="146"
                  className="mt-1"
                  data-testid="input-probe-user-id"
                />
              </div>
              <Button
                onClick={runProbe}
                disabled={isRunning}
                data-testid="button-run-probe"
              >
                {isRunning ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Contacting Kudoz API…
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Run Diagnostic
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {runError && (
          <Card className="border-destructive/40">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-destructive">
                <XCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-medium">Probe request failed: {runError}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {result && (
          <div className="space-y-4">
            {allOk && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-green-600/10 border border-green-600/20 text-green-700 dark:text-green-400">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-medium">All Kudoz API calls succeeded — credentials are working correctly.</p>
              </div>
            )}
            {anyFailed && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-medium">One or more steps failed — check the response bodies below for the exact error from the Kudoz backend.</p>
              </div>
            )}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Credential Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Kudoz API URL</span>
                    <p className="font-mono text-xs mt-0.5 break-all">{result.config.kudoz_api_url}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Active key source</span>
                    <p className="font-medium mt-0.5">
                      {result.config.key_source === 'none' ? (
                        <span className="text-destructive">No key configured</span>
                      ) : (
                        <span className={result.config.key_source === 'KUDOZ_API_KEY' ? 'text-green-600' : 'text-amber-600'}>
                          {result.config.key_source}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Key prefix (first 8 chars)</span>
                    <p className="font-mono text-xs mt-0.5">{result.config.key_prefix}…</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Key length</span>
                    <p className="font-mono text-xs mt-0.5">{result.config.key_length} chars</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">System wallet user ID</span>
                    <p className="font-mono text-xs mt-0.5 font-semibold">{systemUserId}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Step Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StepRow
                  label="Health Check"
                  description={`GET ${result.config.kudoz_api_url}/health`}
                  step={result.steps.health}
                />
                <StepRow
                  label={`GET User (ID: ${result.steps.get_user.userId})`}
                  description={`GET ${result.config.kudoz_api_url}/users/${result.steps.get_user.userId}`}
                  step={result.steps.get_user}
                />
                <StepRow
                  label={`POST User (ID: ${result.steps.post_user.userId})`}
                  description={`POST ${result.config.kudoz_api_url}/users — creates or updates user`}
                  step={result.steps.post_user}
                />
                <StepRow
                  label={`System Wallet (User ${systemUserId})`}
                  description={`GET ${result.config.kudoz_api_url}/wallets/${systemUserId} — Eatzy source wallet`}
                  step={result.steps.system_wallet}
                  extra={
                    result.steps.system_wallet.ok && systemWalletBalance != null ? (
                      <p className="text-xs mt-0.5">
                        <span className="text-muted-foreground">Balance: </span>
                        <span className={`font-semibold ${systemWalletBalance > 0 ? 'text-green-600' : 'text-destructive'}`}>
                          {formatBalance(systemWalletBalance)} Kudoz
                        </span>
                      </p>
                    ) : null
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">System Wallet Actions</CardTitle>
                </div>
                <CardDescription className="text-xs">
                  The active system wallet (user {systemUserId}) is the source of all Kudoz credits issued to users. It must have a non-zero balance for Kudoz claims to succeed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-sm text-muted-foreground">Current balance</p>
                    <p className={`text-2xl font-bold mt-0.5 ${systemWalletBalance != null && systemWalletBalance > 0 ? 'text-green-600' : 'text-destructive'}`}>
                      {systemWalletBalance != null ? `${formatBalance(systemWalletBalance)} Kudoz` : result.steps.system_wallet.ok ? '0 Kudoz' : 'Unknown (wallet step failed)'}
                    </p>
                  </div>
                  <Button
                    onClick={fundSystemWallet}
                    disabled={isFunding}
                    data-testid="button-fund-system-wallet"
                  >
                    {isFunding ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Funding…
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Fund System Wallet (1B Kudoz)
                      </>
                    )}
                  </Button>
                </div>

                {fundResult && (
                  <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-green-600/10 border border-green-600/20 text-green-700 dark:text-green-400">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">System wallet funded successfully</p>
                      <p className="text-xs mt-0.5">
                        Balance: <span className="font-mono">{formatBalance(fundResult.previous_balance)}</span> → <span className="font-mono font-semibold">{formatBalance(fundResult.wallet?.balance)}</span> Kudoz
                        {fundResult.created && <span className="ml-2 opacity-70">(wallet was newly created)</span>}
                      </p>
                    </div>
                  </div>
                )}

                {fundError && (
                  <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Fund request failed</p>
                      <p className="text-xs mt-0.5 font-mono">{fundError}</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Calls <code className="font-mono">POST /wallets/admin/fund</code> with <code className="font-mono">user_id: {systemUserId}, balance: 1000000000</code>. Re-run the diagnostic after funding to confirm the new balance.
                </p>
              </CardContent>
            </Card>

            <Card className="border-blue-500/30 bg-blue-500/5">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4 text-blue-600" />
                  <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-400 uppercase tracking-wide">Production Wallet Migration</CardTitle>
                </div>
                <CardDescription className="text-xs">
                  One-time operation to transition the system wallet from the dev account (53884) to the correct production account (112043). Runs both operations in parallel.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="px-3 py-2 rounded-md bg-muted/40 border">
                    <p className="text-xs text-muted-foreground mb-1">Dev wallet (53884)</p>
                    <p className="font-semibold text-destructive">Set to 0 Kudoz</p>
                  </div>
                  <div className="px-3 py-2 rounded-md bg-muted/40 border">
                    <p className="text-xs text-muted-foreground mb-1">Prod wallet (112043)</p>
                    <p className="font-semibold text-green-600">Set to 1,000,000,000 Kudoz</p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={migrateSystemWallets}
                  disabled={isMigrating}
                  data-testid="button-migrate-system-wallets"
                >
                  {isMigrating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Running migration…
                    </>
                  ) : (
                    <>
                      <ArrowLeftRight className="w-4 h-4 mr-2" />
                      Run Migration
                    </>
                  )}
                </Button>

                {migrateResult && (
                  <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-green-600/10 border border-green-600/20 text-green-700 dark:text-green-400">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Migration completed successfully</p>
                      <p className="text-xs">
                        Dev wallet (53884): <span className="font-mono">{formatBalance(migrateResult.dev_wallet?.previous_balance)}</span> → <span className="font-mono font-semibold">{formatBalance(migrateResult.dev_wallet?.wallet?.balance)}</span> Kudoz
                      </p>
                      <p className="text-xs">
                        Prod wallet (112043): <span className="font-mono">{formatBalance(migrateResult.prod_wallet?.previous_balance)}</span> → <span className="font-mono font-semibold">{formatBalance(migrateResult.prod_wallet?.wallet?.balance)}</span> Kudoz
                      </p>
                    </div>
                  </div>
                )}

                {migrateError && (
                  <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Migration failed</p>
                      <p className="text-xs mt-0.5 font-mono">{migrateError}</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  After running this migration, set <code className="font-mono">KUDOZ_SYSTEM_USER_ID=112043</code> in the <code className="font-mono">kudoz-frontapp-secret</code> k8s secret and restart the pod. Re-run the diagnostic to confirm the active system user ID has changed to 112043.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
